import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface ReunionTransaction {
  id: string;
  property_number: string | null;
  seller: string | null;
  buyer: string | null;
  mandate_date: string | null;
  compromise_date: string | null;
  expected_acte_date: string | null;
  acte_date: string | null;
  sale_price: number | null;
  fees_without_tax: number | null;
  property_type: string | null;
  property_origin: string | null;
  retrocession_types: string[];
  status: string;
  company: string | null;
  agency: string | null;
  /** Agence du négociateur côté vendeur (role 'vendeur' ou 'vendeur_apporteur') */
  listing_agency: string | null;
  /** Agence du négociateur côté acquéreur (role 'apporteur') */
  buyer_agency: string | null;
}

export interface ReunionAgency {
  name: string;
  company: string;
}

export function useReunionMensuelleData(fiscalYear: number) {
  const [transactions, setTransactions] = useState<ReunionTransaction[]>([]);
  const [agencies, setAgencies] = useState<ReunionAgency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [fiscalYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const startDate = `${fiscalYear}-04-01`;
      const endDate = `${fiscalYear + 1}-03-31`;

      const [{ data: txData, error: txError }, { data: agenciesData, error: agenciesError }] = await Promise.all([
        supabase
          .from('transactions')
          .select(`
            id,
            property_number,
            seller,
            buyer,
            mandate_date,
            compromise_date,
            expected_acte_date,
            acte_date,
            sale_price,
            fees_without_tax,
            property_type,
            property_origin,
            status,
            transaction_agencies (
              agency,
              agencies ( name, company )
            ),
            transaction_retrocessions (
              type
            ),
            transaction_negotiators (
              role,
              agency
            )
          `)
          .in('status', ['compromis', 'acte', 'encaisse', 'cloture'])
          .gte('compromise_date', startDate)
          .lte('compromise_date', endDate),
        supabase.from('agencies').select('name, company'),
      ]);

      if (txError) throw txError;
      if (agenciesError) throw agenciesError;

      setTransactions((txData || []).map((t: any) => {
        const negotiators = t.transaction_negotiators || [];
        const vendeurRow = negotiators.find((n: any) => n.role === 'vendeur' || n.role === 'vendeur_apporteur');
        const apporteurRow = negotiators.find((n: any) => n.role === 'apporteur');
        return {
        id: t.id,
        property_number: t.property_number,
        seller: t.seller,
        buyer: t.buyer,
        mandate_date: t.mandate_date,
        compromise_date: t.compromise_date,
        expected_acte_date: t.expected_acte_date,
        acte_date: t.acte_date,
        sale_price: t.sale_price,
        fees_without_tax: t.fees_without_tax,
        property_type: t.property_type,
        property_origin: t.property_origin,
        retrocession_types: (t.transaction_retrocessions || []).map((r: any) => r.type),
        status: t.status,
        company: t.transaction_agencies?.[0]?.agencies?.company ?? null,
        agency: t.transaction_agencies?.[0]?.agency ?? null,
        listing_agency: vendeurRow?.agency ?? null,
        buyer_agency: apporteurRow?.agency ?? null,
        };
      }));

      setAgencies((agenciesData || []) as ReunionAgency[]);
    } catch (err: any) {
      console.error('Error loading réunion mensuelle data:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  return { transactions, agencies, loading, error };
}
